import {
  Stack,
  Title,
  Select,
  useToast,
  CircularLoader,
  useActions
} from "@introist/react-foundation/v2";
import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usersApi } from "../api";
import { useEmployees } from "../../modules/employees/hooks/useEmployees";
import introistLogo from "../../assets/introist-icon-default.png";

interface UserIntroistIdentityProps {
  userId: string;
}

export const UserIntroistIdentity = ({ userId }: UserIntroistIdentityProps) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { onConfirmAction } = useActions();

  // Fetch user identities
  const { data: identities = [], isLoading: identitiesLoading } = useQuery({
    queryKey: ["ai-studio", "users", userId, "identities"],
    queryFn: () => usersApi.listIdentities(userId)
  });

  // Fetch all employees
  const { employees = [] } = useEmployees({}, { enabled: true });

  // Find the introist identity if it exists
  const introistIdentity = useMemo(
    () => identities.find(i => i.external_system === "introist"),
    [identities]
  );

  // Link mutation
  const linkMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      const employee = employees.find(e => e.id === employeeId);
      if (!employee) throw new Error("Employee not found");

      return usersApi.createIdentity(userId, {
        external_system: "introist",
        external_id: employeeId,
        external_name: `${employee.firstname || ""} ${employee.lastname || ""}`.trim()
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["ai-studio", "users", userId, "identities"]
      });
      toast.success("Linked to Introist employee");
    },
    onError: (error: any) => {
      toast.error(`Failed to link employee: ${error.message}`);
    }
  });

  // Unlink mutation
  const unlinkMutation = useMutation({
    mutationFn: async () => {
      if (!introistIdentity) throw new Error("No identity to unlink");
      return usersApi.deleteIdentity(userId, introistIdentity.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["ai-studio", "users", userId, "identities"]
      });
      toast.success("Unlinked from Introist employee");
    },
    onError: (error: any) => {
      toast.error(`Failed to unlink employee: ${error.message}`);
    }
  });

  const handleSelect = async (option: { key: string }) => {
    await linkMutation.mutateAsync(option.key);
  };

  const employeeOptions = useMemo(() => {
    return employees.map(employee => ({
      key: employee.id!,
      title: `${employee.firstname || ""} ${employee.lastname || ""}`.trim() || employee.email!
    }));
  }, [employees]);

  const selectedValue = introistIdentity?.external_id || "";

  const handleUnlink = onConfirmAction(
    async () => {
      await unlinkMutation.mutateAsync();
    },
    {
      title: "Unlink Introist employee",
      description: "Are you sure you want to unlink this user from the Introist employee?",
      confirmTitle: "Unlink"
    }
  );

  return (
    <Stack style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
      <Stack style={{ alignItems: "center", gap: "var(--spacing-small)" }}>
        <img src={introistLogo} alt="Introist" width={20} />
        <Title variant="bold">Introist</Title>
      </Stack>

      <Select
        size="small"
        variant="outlined"
        value={selectedValue}
        onSelect={handleSelect}
        options={employeeOptions}
        endAdornment={
          linkMutation.isLoading || unlinkMutation.isLoading ? (
            <CircularLoader size="tiny" />
          ) : undefined
        }
        placeholder="Link to Introist employee"
        disabled={identitiesLoading}
        style={{ width: "100%" }}
        searchable
        onClear={handleUnlink}
      />
    </Stack>
  );
};
